import {CommonModule} from "@angular/common";
import {Component, DestroyRef, Input, OnInit} from "@angular/core";
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {LibOptionValue} from "daisy-design-system-front";
import {DropdownComponent} from "../../dropdown/dropdown.component";
import {BehaviorSubject, filter, map, merge, mergeMap, Observable, shareReplay, startWith, tap} from "rxjs";
import {ExportResquestForm} from "@gnv-shared/types";
import {ExportRequestFormSummary} from "../../../../models/export-templates/export-template.models";
import {
  InformationMessageDestinationType
} from "../../../../models/information-messages/information-message-destination-type";
import {AdministrationRepo} from "../../../../repositories/administration-repo";
import {UserRepo} from "../../../../repositories/user-repo";
import {ExportRequestFormService} from '@gnv-shared/service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: "app-destination",
  templateUrl: "./destination.component.html",
  styleUrls: ["./destination.component.scss"],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule, DropdownComponent],
})
export class DestinationComponent implements OnInit {
  @Input() requestFormGroup!: FormGroup<ExportResquestForm>;
  @Input() exportRequestFormSummary!: ExportRequestFormSummary;

  public recipientTypeOptions: LibOptionValue<InformationMessageDestinationType>[] = [];
  public dynamicTargets$ = new BehaviorSubject<string[]>([]);
  public optionsValue$!: Observable<any[]>;
  public destinationDisabled$: Observable<boolean>;
  public destinationOptions$!: Observable<LibOptionValue<string>[]>;
  private destinationDisabled = new BehaviorSubject<boolean>(true);

  constructor(
    private readonly translateService: TranslateService,
    private readonly administrationRepo: AdministrationRepo,
    private readonly userRepo: UserRepo,
    private readonly exportRequestFormService: ExportRequestFormService,
    private readonly destroyRef: DestroyRef
  ) {
    this.initRecipientTypes();
    this.destinationDisabled$ = this.destinationDisabled
      .asObservable()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  ngOnInit(): void {
    this.initFormValues();

    const diffusionListOptions$ =
      this.requestFormGroup.controls.destinationType.valueChanges
        .pipe(
          startWith(this.requestFormGroup.controls.destinationType.value),
          filter((informationMessageDestination) => {
              return informationMessageDestination ===
                InformationMessageDestinationType.DIFFUSION_LIST
            }
          ),
          tap(() => this.destinationDisabled.next(true)),
          mergeMap(() =>
            this.userRepo
              .retrieveAllDiffusionList()
              .pipe(
                map(
                  (diffusionListPaged) =>
                    diffusionListPaged.list?.map((diffusionList: any) => ({
                      value: diffusionList.uid,
                      name: diffusionList.name,
                      type: InformationMessageDestinationType.DIFFUSION_LIST,
                    })) || []
                )
              )
          ),
          tap(() => this.destinationDisabled.next(false))
        );

    const userListOptions$ = this.requestFormGroup.controls.destinationType.valueChanges.pipe(
      filter((informationMessageDestination) =>
        informationMessageDestination === InformationMessageDestinationType.USER
      ),
      tap(() => this.destinationDisabled.next(true)),
      mergeMap(() =>
        this.userRepo
          .getUsersList(0, 999, [])
          .pipe(
            map(
              (listPaged) =>
                listPaged.list?.map((user) => ({
                  value: user.uid,
                  name: user.firstName + " " + user.lastName,
                  type: InformationMessageDestinationType.USER,
                })) || []
            )
          )
      ),
      tap(() => this.destinationDisabled.next(false))
    );

    const roleOptions$ = this.requestFormGroup.controls.destinationType.valueChanges.pipe(
      filter(
        (informationMessageDestination) =>
          informationMessageDestination ===
          InformationMessageDestinationType.ROLE
      ),
      tap(() => this.destinationDisabled.next(true)),
      mergeMap(() =>
        this.administrationRepo
          .getAllRoles()
          .pipe(
            map((rolePagedList) => {
              console.log('rolePagedList :: ', rolePagedList);
              return rolePagedList.list?.map((role: any) => ({
                value: role.uid!,
                name: role.name,
                type: InformationMessageDestinationType.ROLE,
              })) || []
            })
          )
      ),
      tap(() => this.destinationDisabled.next(false))
    );

    const dynamicOptions$ = this.requestFormGroup.controls.destinationType
      .valueChanges
      .pipe(
        tap(() => this.destinationDisabled.next(true)),
        mergeMap(() => {
            return this.dynamicTargets$.pipe(
              map((dynamicTargets) =>
                dynamicTargets.map((dynamicTarget) => {
                  const targetToI18nMap: Map<string, string> = new Map([
                    ["TICKETING_ON_CALL", "ticketingOnCall"],
                    ["EXPORT_TARGET", "exportTarget"],
                    ["USER_CONCERNED", "userConcerned"],
                    ["STATION_MANAGER", "stationManager"],
                    ["BADGE_OWNER", "badgeOwner"],
                    [
                      "SUBSCRIPTION_CONTACT_INFORMATION",
                      "subscriptionContactInformationStation",
                    ],
                    [
                      "SUBSCRIPTION_CONTACT_RESPONSABLE_CONTRAT",
                      "subscriptionContactResponsableContrat",
                    ],
                    ["SUBSCRIPTION_MANAGER", "subscriptionManager"],
                    ["USER_STATION_FAVORITES", "userStationFavorites"],
                  ]);
                  const targetI18n = targetToI18nMap.get(dynamicTarget) ?? "other";
                  return {
                    value: dynamicTarget,
                    name: this.translateService.instant(
                      `notifications.destination.${targetI18n}`
                    ),
                    type: InformationMessageDestinationType.DYNAMIC,
                  };
                })
              )
            )
          }
        ),
        tap(() => this.destinationDisabled.next(false))
      );

    this.optionsValue$ = merge(
      diffusionListOptions$,
      roleOptions$,
      userListOptions$,
      dynamicOptions$
    ).pipe(startWith([]));

    this.destinationOptions$ = this.optionsValue$
      .pipe(
        map(items => items.map(item => ({ label: item.name, value: item.value })))
      );
  }

  initFormValues() {
    if (this.requestFormGroup.controls.destinationType.value) {
      setTimeout(() => {
        this.requestFormGroup.controls.destinationType.patchValue(this.requestFormGroup.controls.destinationType.value);
      });
    }

    this.requestFormGroup.controls.destinationValues.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(selectedValues => {
        this.exportRequestFormService.setDestinationFormValues(selectedValues)
      });
  }

  public onDestinationTypeChanged() {
    this.requestFormGroup.controls.destinationValues.reset([], { emitEvent: false });
  }

  private initRecipientTypes() {
    this.recipientTypeOptions = [
      {
        label: this.translateService.instant('notifications.destinationTypes.diffusionList'),
        value: InformationMessageDestinationType.DIFFUSION_LIST
      },
      {
        label: this.translateService.instant('notifications.destinationTypes.role'),
        value: InformationMessageDestinationType.ROLE
      },
      {
        label: this.translateService.instant('notifications.destinationTypes.user'),
        value: InformationMessageDestinationType.USER
      }
    ]
  }
}
